if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mobileMql = window.matchMedia('(max-width: 749px)');
        this.mobileGalleryInitialized = false;
        this.isResettingInfiniteScroll = false;
        this.initialMobilePositionSet = false;

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        }

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }

        this.mobileMql.addEventListener('change', () => this.handleMobileGalleryViewport());
        this.mql.addEventListener('change', () => this.handleMobileGalleryViewport());
      }

      connectedCallback() {
        this.initMobileDotsGallery();
      }

      handleMobileGalleryViewport() {
        if (this.mobileMql.matches) {
          this.initMobileDotsGallery();
        } else {
          this.destroyMobileDotsGallery();
        }
      }

      initMobileDotsGallery() {
        if (!this.mobileMql.matches || this.dataset.mobilePagination !== 'dots') return;
        if (!this.elements.viewer?.classList.contains('product-media-pagination--dots')) return;

        this.slider = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        if (!this.slider) return;

        this.realSlides = [...this.slider.querySelectorAll('.product__media-item[id^="Slide-"]')];
        this.dotLinks = [...this.elements.viewer.querySelectorAll('.product-media-pagination-dots .slider-counter__link')];
        this.prevButton = this.elements.viewer.querySelector('.slider-buttons--counter button[name="previous"]');
        this.nextButton = this.elements.viewer.querySelector('.slider-buttons--counter button[name="next"]');

        if (this.realSlides.length < 2) return;

        this.elements.viewer.classList.remove('is-gallery-ready');
        this.destroyMobileDotsGallery(false);
        this.realSlides = [...this.slider.querySelectorAll('.product__media-item[id^="Slide-"]')];
        this.setupInfiniteClones();
        this.initialMobilePositionSet = false;
        this.isResettingInfiniteScroll = true;
        this.slider.classList.add('product__media-list--positioning');
        this.bindMobileGalleryEvents();
        this.mobileGalleryInitialized = true;
        this.finishInitialPosition();
      }

      finishInitialPosition() {
        const complete = () => {
          this.positionToRealSlide(0, false);
          this.slider.classList.remove('product__media-list--positioning');
          this.isResettingInfiniteScroll = false;
          this.initialMobilePositionSet = true;
          this.setActiveDot(0);
          this.elements.viewer.classList.add('is-gallery-ready');
        };

        this.positionToRealSlide(0, false);
        requestAnimationFrame(() => {
          this.positionToRealSlide(0, false);
          requestAnimationFrame(complete);
        });
      }

      destroyMobileDotsGallery(resetScroll = true) {
        if (!this.slider) return;

        this.slider.querySelectorAll('.product__media-item--clone').forEach((slide) => slide.remove());
        this.slider.classList.remove('product__media-list--positioning');
        this.elements.viewer?.classList.remove('is-gallery-ready');

        if (this.mobileScrollHandler) {
          this.slider.removeEventListener('scroll', this.mobileScrollHandler);
        }
        if (this.mobileScrollEndHandler) {
          this.slider.removeEventListener('scrollend', this.mobileScrollEndHandler);
        }
        if (this.mobileScrollPolyfillHandler) {
          this.slider.removeEventListener('scroll', this.mobileScrollPolyfillHandler);
        }
        if (this.mobileNavCaptureHandler) {
          this.elements.viewer.removeEventListener('click', this.mobileNavCaptureHandler, true);
        }
        if (this.mobileDotHandlers) {
          this.mobileDotHandlers.forEach(({ link, handler }) => link.removeEventListener('click', handler, true));
        }
        if (this.mobileResizeObserver) {
          this.mobileResizeObserver.disconnect();
        }
        clearTimeout(this.mobileResizeTimer);
        clearTimeout(this.mobileScrollEndTimer);

        this.mobileGalleryInitialized = false;
        this.initialMobilePositionSet = false;

        if (resetScroll && this.realSlides?.length) {
          this.slider.scrollLeft = 0;
        }
      }

      setupInfiniteClones() {
        const lastClone = this.realSlides[this.realSlides.length - 1].cloneNode(true);
        lastClone.classList.add('product__media-item--clone');
        lastClone.removeAttribute('id');
        lastClone.removeAttribute('data-media-id');
        lastClone.setAttribute('aria-hidden', 'true');
        lastClone.setAttribute('tabindex', '-1');

        const firstClone = this.realSlides[0].cloneNode(true);
        firstClone.classList.add('product__media-item--clone');
        firstClone.removeAttribute('id');
        firstClone.removeAttribute('data-media-id');
        firstClone.setAttribute('aria-hidden', 'true');
        firstClone.setAttribute('tabindex', '-1');

        this.slider.insertBefore(lastClone, this.realSlides[0]);
        this.slider.appendChild(firstClone);
      }

      bindMobileGalleryEvents() {
        this.mobileScrollHandler = () => {
          if (this.isResettingInfiniteScroll || !this.initialMobilePositionSet) return;
          this.updateDotsFromScroll();
          if (this.dataset.mobilePaginationArrows === 'true') {
            this.enableInfiniteNavButtons();
          }
        };

        this.mobileScrollEndHandler = () => this.handleInfiniteScrollReset();

        this.mobileNavCaptureHandler = (event) => {
          const button = event.target.closest('.slider-buttons--counter .slider-button');
          if (!button || !this.mobileMql.matches) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          if (button.name === 'previous') {
            this.goToRelative(-1);
          } else if (button.name === 'next') {
            this.goToRelative(1);
          }
        };

        this.slider.addEventListener('scroll', this.mobileScrollHandler, { passive: true });
        this.slider.addEventListener('scrollend', this.mobileScrollEndHandler);

        if (!('onscrollend' in window)) {
          this.mobileScrollPolyfillHandler = () => {
            clearTimeout(this.mobileScrollEndTimer);
            this.mobileScrollEndTimer = setTimeout(() => this.handleInfiniteScrollReset(), 100);
          };
          this.slider.addEventListener('scroll', this.mobileScrollPolyfillHandler, { passive: true });
        }

        if (this.dataset.mobilePaginationArrows === 'true') {
          this.elements.viewer.addEventListener('click', this.mobileNavCaptureHandler, true);
        }

        this.mobileDotHandlers = this.dotLinks.map((link, index) => {
          const handler = (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.scrollToSlide(index, true);
          };
          link.addEventListener('click', handler, true);
          return { link, handler };
        });

        this.mobileResizeObserver = new ResizeObserver(() => {
          if (!this.mobileGalleryInitialized || !this.initialMobilePositionSet) return;
          clearTimeout(this.mobileResizeTimer);
          this.mobileResizeTimer = setTimeout(() => {
            this.positionToRealSlide(this.getActiveSlideIndex(), false);
          }, 150);
        });
        this.mobileResizeObserver.observe(this.slider);
      }

      getSliderCenter() {
        const rect = this.slider.getBoundingClientRect();
        return rect.left + rect.width / 2;
      }

      getElementCenter(element) {
        const rect = element.getBoundingClientRect();
        return rect.left + rect.width / 2;
      }

      getCenteredElement() {
        const center = this.getSliderCenter();
        let centeredElement = null;
        let closestDistance = Infinity;

        this.slider.querySelectorAll('.product__media-item').forEach((element) => {
          const distance = Math.abs(this.getElementCenter(element) - center);
          if (distance < closestDistance) {
            closestDistance = distance;
            centeredElement = element;
          }
        });

        return centeredElement;
      }

      getActiveSlideIndex() {
        if (!this.initialMobilePositionSet) return 0;

        const centeredElement = this.getCenteredElement();
        if (!centeredElement) return 0;

        if (centeredElement.classList.contains('product__media-item--clone')) {
          return centeredElement === this.slider.firstElementChild ? this.realSlides.length - 1 : 0;
        }

        const index = this.realSlides.indexOf(centeredElement);
        return index >= 0 ? index : 0;
      }

      getScrollLeftForElement(element) {
        return element.offsetLeft - (this.slider.clientWidth - element.clientWidth) / 2;
      }

      scrollToElement(element, animate = false) {
        if (!element || !this.slider) return;

        const targetLeft = this.getScrollLeftForElement(element);

        if (animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.slider.scrollTo({ left: targetLeft, behavior: 'smooth' });
        } else {
          this.slider.scrollLeft = targetLeft;
        }
      }

      positionToRealSlide(index, animate = false) {
        const slide = this.realSlides[index];
        if (!slide) return;

        this.scrollToElement(slide, animate);
        this.setActiveDot(index);
      }

      scrollToSlide(index, animate = true) {
        this.positionToRealSlide(index, animate);
      }

      goToRelative(direction) {
        const activeIndex = this.getActiveSlideIndex();

        if (direction === -1 && activeIndex === 0) {
          const prependedClone = this.slider.firstElementChild;
          if (prependedClone?.classList.contains('product__media-item--clone')) {
            this.scrollToElement(prependedClone, true);
            return;
          }
        }

        if (direction === 1 && activeIndex === this.realSlides.length - 1) {
          const appendedClone = this.slider.lastElementChild;
          if (appendedClone?.classList.contains('product__media-item--clone')) {
            this.scrollToElement(appendedClone, true);
            return;
          }
        }

        const nextIndex = (activeIndex + direction + this.realSlides.length) % this.realSlides.length;
        this.scrollToSlide(nextIndex, true);
      }

      updateDotsFromScroll() {
        this.setActiveDot(this.getActiveSlideIndex());
      }

      setActiveDot(index) {
        if (!this.dotLinks.length) return;

        this.dotLinks.forEach((link, linkIndex) => {
          const isActive = linkIndex === index;
          link.classList.toggle('slider-counter__link--active', isActive);
          if (isActive) {
            link.setAttribute('aria-current', 'true');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      }

      enableInfiniteNavButtons() {
        if (this.prevButton) this.prevButton.removeAttribute('disabled');
        if (this.nextButton) this.nextButton.removeAttribute('disabled');
      }

      handleInfiniteScrollReset() {
        if (this.isResettingInfiniteScroll || !this.initialMobilePositionSet) return;

        const centeredElement = this.getCenteredElement();
        if (!centeredElement?.classList.contains('product__media-item--clone')) return;

        const targetIndex = centeredElement === this.slider.firstElementChild ? this.realSlides.length - 1 : 0;

        this.isResettingInfiniteScroll = true;
        this.slider.classList.add('product__media-list--positioning');
        this.positionToRealSlide(targetIndex, false);

        requestAnimationFrame(() => {
          this.slider.classList.remove('product__media-list--positioning');
          this.isResettingInfiniteScroll = false;
          this.setActiveDot(targetIndex);
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.resetPages) this.elements.viewer.resetPages();
          if (this.mobileGalleryInitialized) this.initMobileDotsGallery();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.mobileGalleryInitialized && this.mobileMql.matches) {
            const slideIndex = this.realSlides.indexOf(activeMedia);
            if (slideIndex >= 0) {
              this.positionToRealSlide(slideIndex, false);
              return;
            }
          }

          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
